import { cva, type VariantProps } from 'class-variance-authority'
import type { HTMLAttributes, ReactNode } from 'react'
import { Button as AriaButton } from 'react-aria-components'
import { cn } from '../../lib/cn'
import { focusRing } from '../../lib/focus-ring'
import { Icon } from '../../atoms/icon/icon'

/**
 * The Toast set (445:45123): Property 1 = Success (445:45122) / Warning
 * (445:45121). A 430px card filled with the Success or Warning colour, a 24px
 * white badge icon, a Body SemiBold title over a Body message in white, and an
 * 18px white close cross.
 *
 * The action is the component's hidden "Button Area" (445:45117), shown when an
 * `action` is passed: "Action button/Variant2" (445:45118) beside the close cross,
 * 16px apart in the right column, a white button with a 1px 60% white stroke, 6px
 * radius, 12px sides and 10px top and bottom, its label in Poppins Regular 12/1 in
 * Success. The Warning toast draws the same Success label (445:45099).
 *
 * Keyboard focus takes the kit's focus ring in Light, so it shows on the coloured
 * fill; it is not drawn in the Figma.
 */
export const toastVariants = cva(
	'flex items-start justify-between overflow-clip rounded-atlas-md p-5 font-atlas-sans text-atlas-light',
	{
		variants: {
			tone: {
				success: 'bg-atlas-success',
				warning: 'bg-atlas-warning',
			},
		},
		defaultVariants: { tone: 'success' },
	},
)

export type ToastTone = NonNullable<VariantProps<typeof toastVariants>['tone']>

/** The toast's one action. */
export interface ToastAction {
	label: string
	onPress?: () => void
}

const controlClass = cn(focusRing, 'data-focus-visible:outline-atlas-light')

export interface ToastProps
	extends Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'title'>,
		VariantProps<typeof toastVariants> {
	title: ReactNode
	message?: ReactNode
	/** The action button beside the close cross, e.g. "View"; the Figma hides it by default. */
	action?: ToastAction
	/** Called when the close cross is pressed. The cross is drawn on every toast. */
	onClose?: () => void
	/** Accessible name of the close cross. */
	closeLabel?: string
}

/**
 * A presentational toast. It does not time out or stack: the app's notification
 * surface owns that and renders this. Success is announced politely
 * (`role="status"`), Warning assertively (`role="alert"`).
 */
export function Toast({
	tone,
	title,
	message,
	action,
	onClose,
	closeLabel = 'Close',
	className,
	...props
}: ToastProps) {
	const resolved = tone ?? 'success'
	return (
		<div
			role={resolved === 'warning' ? 'alert' : 'status'}
			{...props}
			className={cn(toastVariants({ tone: resolved }), 'w-107.5', className)}
		>
			<div className="flex min-w-0 flex-1 items-start gap-3">
				<Icon name={resolved === 'warning' ? 'toast-warning' : 'toast-success'} />
				<div className="flex min-w-0 flex-1 flex-col justify-center gap-1 self-stretch">
					<p className="whitespace-nowrap text-atlas-body-semibold">{title}</p>
					{message ? <p className="text-atlas-body">{message}</p> : null}
				</div>
			</div>
			<div className="flex shrink-0 items-start gap-4 self-stretch">
				{action ? (
					<div className="flex h-10.75 flex-col items-center justify-center">
						<AriaButton
							onPress={action.onPress}
							className={cn(
								'inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-atlas-action bg-atlas-light px-3 py-2.5 font-atlas-poppins text-atlas-toast-action text-atlas-success inset-ring inset-ring-atlas-light/60',
								controlClass,
							)}
						>
							{action.label}
						</AriaButton>
					</div>
				) : null}
				<AriaButton
					aria-label={closeLabel}
					onPress={onClose}
					className={cn('inline-flex cursor-pointer rounded-atlas-sm', controlClass)}
				>
					<Icon name="close" />
				</AriaButton>
			</div>
		</div>
	)
}
